import React, { useEffect, useState } from "react";
import { api, extractError } from "../lib/api";
import { formatKg, formatWeightKg, kgToQtl, qtlToKg } from "../lib/weightUnits";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Textarea } from "./ui/textarea";
import { toast } from "sonner";
import { canWeighCorrectPurchase, purchaseHasFinalizedAccounts } from "../lib/unloadingHelpers";
import { useAuth, hasRole } from "../context/AuthContext";

/**
 * Re-enter or correct gross / mid / tare bridge weights (weighbridge / admin).
 * Post-finalize corrections reopen the purchase for accounts review.
 */
export default function AdminWeighCorrection({ purchase, onSaved, compact = false }) {
  const { user } = useAuth();
  const canCorrect = hasRole(user, "weighbridge") && canWeighCorrectPurchase(purchase);
  const willReopen = purchaseHasFinalizedAccounts(purchase);
  const segments = purchase?.loose_weigh_segments || [];
  const midSegments = segments
    .map((s, i) => ({ ...s, index: i }))
    .filter((s) => s.kind === "mid");

  const [kind, setKind] = useState("gross");
  const [weight, setWeight] = useState("");
  const [segmentIndex, setSegmentIndex] = useState(
    midSegments.length ? String(midSegments[midSegments.length - 1].index) : "",
  );
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (kind === "gross") {
      const kg = qtlToKg(purchase?.gross_weight_qtl);
      setWeight(kg != null ? String(kg) : "");
    } else if (kind === "tare") {
      const kg = qtlToKg(purchase?.tare_weight_qtl);
      setWeight(kg != null ? String(kg) : "");
    } else if (midSegments.length) {
      const idx = segmentIndex !== "" ? Number(segmentIndex) : midSegments[midSegments.length - 1].index;
      const seg = segments[idx];
      const kg = qtlToKg(seg?.weight_qtl);
      setWeight(kg != null ? String(kg) : "");
    } else {
      setWeight("");
    }
  }, [kind, purchase?.id, purchase?.gross_weight_qtl, purchase?.tare_weight_qtl, segmentIndex, segments.length]);

  const submit = async (e) => {
    e.preventDefault();
    const wKg = parseFloat(weight);
    if (!Number.isFinite(wKg) || wKg <= 0) {
      toast.error("Enter a valid weight in kg");
      return;
    }
    const wQtl = kgToQtl(wKg);
    if (wQtl == null) {
      toast.error("Enter a valid weight in kg");
      return;
    }
    if (kind === "mid" && segmentIndex === "") {
      toast.error("Select which mid weigh segment to correct");
      return;
    }
    try {
      setSaving(true);
      await api.patch(`/purchases/${purchase.id}/weigh-correction`, {
        kind,
        weight_qtl: wQtl,
        segment_index: kind === "mid" ? Number(segmentIndex) : undefined,
        note: note.trim() || undefined,
      });
      toast.success(`${kind.toUpperCase()} weight corrected (${formatKg(wKg)} kg)`);
      setNote("");
      onSaved?.();
    } catch (err) {
      toast.error(extractError(err));
    } finally {
      setSaving(false);
    }
  };

  const blocked = !canCorrect;
  const blockedReason = purchase?.state === "REJECTED"
    ? `Purchase is ${purchase.state} — weights cannot be changed.`
    : "Voucher is pending first-time finalization — use accounts to adjust before saving bridge corrections.";

  return (
    <div
      className={`rounded-md border border-amber-300 bg-amber-50/60 ${compact ? "p-3" : "p-4"}`}
      data-testid="admin-weigh-correction"
    >
      <p className="text-xs font-medium text-amber-950">Correct / re-enter weighbridge weights</p>
      <p className="text-[10px] text-amber-900/80 mt-1 leading-relaxed">
        Fix gross, mid, or tare if the wrong reading was recorded (enter kg). Net weight, unloading destinations, and
        contract splits are recalculated automatically.
      </p>

      <dl className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-3 text-[10px] tabular">
        <div>
          <dt className="text-muted-foreground uppercase">Gross</dt>
          <dd className="font-medium">{formatWeightKg(purchase?.gross_weight_qtl) ?? "—"}</dd>
        </div>
        <div>
          <dt className="text-muted-foreground uppercase">Tare</dt>
          <dd className="font-medium">{formatWeightKg(purchase?.tare_weight_qtl) ?? "—"}</dd>
        </div>
        <div>
          <dt className="text-muted-foreground uppercase">Net</dt>
          <dd className="font-medium text-primary">{formatWeightKg(purchase?.net_weight_qtl) ?? "—"}</dd>
        </div>
        <div>
          <dt className="text-muted-foreground uppercase">Mid weighs</dt>
          <dd className="font-medium">{midSegments.length || "—"}</dd>
        </div>
      </dl>

      {segments.length > 0 && (
        <div className="mt-2 text-[10px] text-muted-foreground flex flex-wrap gap-2">
          {segments.map((s, i) => (
            <span key={i} className="bg-card border border-border rounded px-1.5 py-0.5 font-mono">
              {s.kind} #{i + 1}: {formatWeightKg(s.weight_qtl)}→{formatWeightKg(s.segment_net_qtl)}
            </span>
          ))}
        </div>
      )}

      {blocked ? (
        <p className="text-xs text-amber-800 mt-3">{blockedReason}</p>
      ) : (
        <>
          {willReopen && (
            <p className="text-xs text-amber-800 mt-3">
              This purchase was finalized. Saving a correction moves the voucher back to{" "}
              <strong>accounts pending</strong> for re-review.
            </p>
          )}
        <form onSubmit={submit} className={`mt-3 grid gap-3 ${compact ? "grid-cols-1" : "grid-cols-1 sm:grid-cols-2 lg:grid-cols-4"}`}>
          <div>
            <Label className="text-[10px] uppercase text-muted-foreground">Weight type</Label>
            <Select value={kind} onValueChange={setKind}>
              <SelectTrigger className="mt-1 h-9"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="gross">Gross (on arrival)</SelectItem>
                <SelectItem value="mid" disabled={midSegments.length === 0}>Mid (segment)</SelectItem>
                <SelectItem value="tare">Tare / final</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {kind === "mid" && (
            <div>
              <Label className="text-[10px] uppercase text-muted-foreground">Mid segment</Label>
              <Select value={segmentIndex} onValueChange={setSegmentIndex}>
                <SelectTrigger className="mt-1 h-9"><SelectValue placeholder="Select segment…" /></SelectTrigger>
                <SelectContent>
                  {midSegments.map((s) => (
                    <SelectItem key={s.index} value={String(s.index)}>
                      Mid #{s.index + 1} — was {formatWeightKg(s.weight_qtl)} (net {formatWeightKg(s.segment_net_qtl)})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
          <div>
            <Label className="text-[10px] uppercase text-muted-foreground">Bridge reading (kg)</Label>
            <Input
              type="number"
              step="1"
              min="0"
              className="mt-1 h-9"
              value={weight}
              onChange={(e) => setWeight(e.target.value)}
              data-testid="admin-weigh-correction-input"
            />
          </div>
          {!compact && (
            <div className="sm:col-span-2">
              <Label className="text-[10px] uppercase text-muted-foreground">Reason (optional)</Label>
              <Textarea
                value={note}
                onChange={(e) => setNote(e.target.value)}
                rows={2}
                className="mt-1 text-xs"
                placeholder="e.g. scale misread, re-weighed at bridge…"
              />
            </div>
          )}
          <div className={compact ? "" : "sm:col-span-2 lg:col-span-4 flex justify-end"}>
            <Button
              type="submit"
              disabled={saving}
              variant="outline"
              className="border-amber-600 text-amber-950 hover:bg-amber-100 min-h-9"
              data-testid="admin-weigh-correction-submit"
            >
              {saving ? "Saving…" : `Save ${kind} correction`}
            </Button>
          </div>
        </form>
        </>
      )}
    </div>
  );
}
